// src/pages/stats/EvolutionMembres.jsx
import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Spinner, Alert, Button, Badge, Table, Pagination, ProgressBar } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { formatNumber, formatDate } from '../../utils/formatters';
import Header from '../../layouts/Header';
import Footer from '../../layouts/Footer';
import { COLORS } from '../../constants/colors';

// ===== PALETTE DE COULEURS POUR LE GRAPHIQUE =====
const CHART_COLORS = [
  COLORS.primary,
  COLORS.secondary,
  COLORS.success,
  COLORS.info,
  COLORS.purple,
  COLORS.pink,
  COLORS.cyan,
  COLORS.indigo,
  COLORS.emerald,
  COLORS.amber
];

export default function EvolutionMembres() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [timeRange, setTimeRange] = useState('12mois');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        navigate('/pages/signin2');
        return;
      }

      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      
      const response = await fetch(`https://josephsbusiness.com/api/stats_evolution_membres.php?user_id=${userId}`, {
        headers: { 'Accept': 'application/json' }
      });

      const textResponse = await response.text();

      if (!textResponse || textResponse.trim() === '') {
        throw new Error("Le serveur a retourné une réponse vide");
      }

      let result;
      try {
        result = JSON.parse(textResponse);
      } catch (e) {
        throw new Error(`Erreur de parsing JSON: ${e.message}`);
      }

      if (result.success) {
        setData(result);
        setCurrentPage(1);
      } else {
        setError(result.message || 'Erreur de chargement');
      }
    } catch (err) {
      console.error('❌ Erreur:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = data?.nouveaux_recents?.slice(indexOfFirstItem, indexOfLastItem) || [];
  const totalPages = Math.ceil((data?.nouveaux_recents?.length || 0) / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    
    let items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
      }
    }

    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <Pagination.Item 
          key={number} 
          active={number === currentPage}
          onClick={() => handlePageChange(number)}
        >
          {number}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
      }
      items.push(
        <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }

    return items;
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
            <div className="text-center">
              <Spinner animation="border" variant="primary" style={{ width: '4rem', height: '4rem' }} />
              <h5 className="fw-bold mt-3" style={{ color: COLORS.primaryDark }}>Chargement des statistiques</h5>
              <p className="text-muted">Récupération des données en cours...</p>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
          <Alert variant="danger" className="border-0 shadow-sm rounded-3">
            <div className="d-flex align-items-center">
              <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
              <div>
                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                <p className="mb-0 small">{error}</p>
                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchData}>
                  <i className="ri-refresh-line me-1"></i>Réessayer
                </Button>
              </div>
            </div>
          </Alert>
        </div>
        <Footer />
      </>
    );
  }

  if (!data || !data.stats) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
          <div className="text-center py-5">
            <i className="ri-inbox-line fs-1 text-muted mb-3 d-block"></i>
            <h5 className="fw-semibold">Aucune donnée disponible</h5>
            <p className="text-muted small mb-3">Aucun nouveau membre trouvé</p>
            <Button variant="primary" size="sm" className="rounded-pill" onClick={fetchData}>
              <i className="ri-refresh-line me-1"></i> Réessayer
            </Button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const chartData = data.evolution_mensuelle?.mois?.map((mois, index) => ({
    mois,
    inscriptions: data.evolution_mensuelle.inscriptions[index] || 0
  })) || [];

  // ===== RENDU PRINCIPAL =====
  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)' }}>
        <div className="container-fluid px-0 px-lg-3">

          {/* ===== EN‑TÊTE MODERNE ===== */}
          <div className="header-modern mb-4 fade-in">
            <div className="header-left">
              <div className="header-icon-wrapper" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.indigo})` }}>
                <i className="ri-user-add-line"></i>
              </div>
              <div>
                <h5 className="gradient-text">Évolution des membres</h5>
                <div className="d-flex flex-wrap gap-2">
                  <span className="badge-modern">
                    <i className="ri-user-add-line"></i> Total {formatNumber(data.stats.total)}
                  </span>
                  <span className="badge-modern" style={{ background: `${COLORS.success}20`, color: COLORS.success }}>
                    <i className="ri-calendar-line"></i> 30j {formatNumber(data.stats.nouveaux_30j)}
                  </span>
                  <span className="badge-modern" style={{ background: `${COLORS.info}20`, color: COLORS.info }}>
                    <i className="ri-calendar-line"></i> 7j {formatNumber(data.stats.nouveaux_7j)}
                  </span>
                </div>
              </div>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <div className="btn-group btn-group-sm" style={{ background: '#f8fafc', padding: '2px', borderRadius: '30px' }}>
                <Button 
                  size="sm"
                  variant={timeRange === '12mois' ? 'primary' : 'outline-primary'}
                  onClick={() => setTimeRange('12mois')}
                  style={{ borderRadius: '30px', padding: '0.2rem 1rem', fontSize: '0.75rem' }}
                >
                  12 mois
                </Button>
                <Button 
                  size="sm"
                  variant={timeRange === '6mois' ? 'primary' : 'outline-primary'}
                  onClick={() => setTimeRange('6mois')}
                  style={{ borderRadius: '30px', padding: '0.2rem 1rem', fontSize: '0.75rem' }}
                >
                  6 mois
                </Button>
              </div>
              <button className="btn-modern-outline" onClick={fetchData} disabled={loading}>
                <i className={`ri-refresh-line ${loading ? 'fa-spin' : ''}`}></i> Actualiser
              </button>
              <button className="btn-modern-outline" onClick={() => navigate('/stats/dashboard')}>
                <i className="ri-arrow-left-line"></i> Retour
              </button>
            </div>
          </div>

          {/* ===== KPI CARDS ===== */}
          <Row className="g-3 mb-4">
            <Col xs="6" xl="3">
              <div className="kpi-card kpi-primary">
                <div className="kpi-icon"><i className="ri-user-add-line fs-24"></i></div>
                <div className="kpi-content">
                  <span className="kpi-label">Total nouveaux</span>
                  <h3 className="kpi-value">{formatNumber(data.stats.total)}</h3>
                </div>
              </div>
            </Col>
            <Col xs="6" xl="3">
              <div className="kpi-card" style={{ borderLeftColor: COLORS.info }}>
                <div className="kpi-icon" style={{ background: 'rgba(59,130,246,0.1)', color: COLORS.info }}>
                  <i className="ri-men-line fs-24"></i>
                </div>
                <div className="kpi-content">
                  <span className="kpi-label">Hommes</span>
                  <h3 className="kpi-value">{formatNumber(data.stats.hommes)}</h3>
                  <small className="text-muted" style={{ fontSize: '0.65rem' }}>
                    {data.stats.total > 0 ? Math.round((data.stats.hommes / data.stats.total) * 100) : 0}%
                  </small>
                </div>
              </div>
            </Col>
            <Col xs="6" xl="3">
              <div className="kpi-card" style={{ borderLeftColor: COLORS.pink }}>
                <div className="kpi-icon" style={{ background: 'rgba(236,72,153,0.1)', color: COLORS.pink }}>
                  <i className="ri-women-line fs-24"></i>
                </div>
                <div className="kpi-content">
                  <span className="kpi-label">Femmes</span>
                  <h3 className="kpi-value">{formatNumber(data.stats.femmes)}</h3>
                  <small className="text-muted" style={{ fontSize: '0.65rem' }}>
                    {data.stats.total > 0 ? Math.round((data.stats.femmes / data.stats.total) * 100) : 0}%
                  </small>
                </div>
              </div>
            </Col>
            <Col xs="6" xl="3">
              <div className="kpi-card" style={{ borderLeftColor: COLORS.warning }}>
                <div className="kpi-icon" style={{ background: 'rgba(245,158,11,0.1)', color: COLORS.warning }}>
                  <i className="ri-user-line fs-24"></i>
                </div>
                <div className="kpi-content">
                  <span className="kpi-label">Enfants</span>
                  <h3 className="kpi-value">{formatNumber(data.stats.enfants)}</h3>
                  <small className="text-muted" style={{ fontSize: '0.65rem' }}>
                    {data.stats.total > 0 ? Math.round((data.stats.enfants / data.stats.total) * 100) : 0}%
                  </small>
                </div>
              </div>
            </Col>
          </Row>

          {/* ===== PÉRIODES ===== */}
          <Row className="g-3 mb-4">
            <Col md={4}>
              <Card className="stat-card-modern" style={{ border: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <Card.Body className="p-3">
                  <div className="d-flex align-items-center gap-3">
                    <div className="stat-icon-modern" style={{ 
                      width: '40px', height: '40px', borderRadius: '10px',
                      background: `linear-gradient(135deg, ${COLORS.success}20, ${COLORS.success}10)`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <i className="ri-calendar-line" style={{ color: COLORS.success, fontSize: '1.2rem' }}></i>
                    </div>
                    <div>
                      <small className="text-muted d-block" style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Nouveaux (30j)</small>
                      <span className="fw-bold" style={{ color: COLORS.success, fontSize: '1.3rem' }}>{formatNumber(data.stats.nouveaux_30j)}</span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="stat-card-modern" style={{ border: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <Card.Body className="p-3">
                  <div className="d-flex align-items-center gap-3">
                    <div className="stat-icon-modern" style={{ 
                      width: '40px', height: '40px', borderRadius: '10px',
                      background: `linear-gradient(135deg, ${COLORS.info}20, ${COLORS.info}10)`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <i className="ri-calendar-line" style={{ color: COLORS.info, fontSize: '1.2rem' }}></i>
                    </div>
                    <div>
                      <small className="text-muted d-block" style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Nouveaux (7j)</small>
                      <span className="fw-bold" style={{ color: COLORS.info, fontSize: '1.3rem' }}>{formatNumber(data.stats.nouveaux_7j)}</span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="stat-card-modern" style={{ border: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <Card.Body className="p-3">
                  <div className="d-flex align-items-center gap-3">
                    <div className="stat-icon-modern" style={{ 
                      width: '40px', height: '40px', borderRadius: '10px',
                      background: `linear-gradient(135deg, ${COLORS.warning}20, ${COLORS.warning}10)`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <i className="ri-sun-line" style={{ color: COLORS.warning, fontSize: '1.2rem' }}></i>
                    </div>
                    <div>
                      <small className="text-muted d-block" style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Aujourd'hui</small>
                      <span className="fw-bold" style={{ color: COLORS.warning, fontSize: '1.3rem' }}>{formatNumber(data.stats.nouveaux_aujourdhui)}</span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* ===== GRAPHIQUE ===== */}
          <Row className="g-3 mb-4">
            <Col md={12}>
              <Card className="chart-card-modern" style={{ 
                border: 'none', borderRadius: '0px', 
                background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(12px)',
                boxShadow: '0 10px 30px rgba(0,0,0,0.05)'
              }}>
                <Card.Header style={{ background: 'rgba(255,255,255,0.5)', borderBottom: '1px solid rgba(0,0,0,0.04)', padding: '0.75rem 1.25rem' }}>
                  <h6 className="fw-semibold mb-0" style={{ color: COLORS.primary, fontSize: '0.95rem' }}>
                    <i className="ri-line-chart-line me-2" style={{ color: COLORS.primary }}></i>
                    Évolution des inscriptions
                    <small className="text-muted ms-2" style={{ fontSize: '0.75rem' }}>Nombre de nouveaux membres par mois</small>
                  </h6>
                </Card.Header>
                <Card.Body style={{ padding: '1rem', height: '300px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                      <XAxis dataKey="mois" tick={{ fontSize: 11, fill: '#64748b' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                      <Tooltip 
                        contentStyle={{ 
                          fontSize: '12px', 
                          borderRadius: '8px',
                          border: 'none',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}
                        formatter={(value) => [formatNumber(value), 'Nouveaux']}
                      />
                      <Bar dataKey="inscriptions" radius={[4, 4, 0, 0]} maxBarSize={40}>
                        {chartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* ===== TABLEAU ===== */}
          <Row className="g-3">
            <Col md={12}>
              <Card className="table-card-modern" style={{ 
                border: 'none', borderRadius: '0px',
                background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(12px)',
                boxShadow: '0 10px 30px rgba(0,0,0,0.05)'
              }}>
                <Card.Header style={{ background: 'rgba(255,255,255,0.5)', borderBottom: '1px solid rgba(0,0,0,0.04)', padding: '0.75rem 1.25rem' }}>
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="fw-semibold mb-0" style={{ color: COLORS.primary, fontSize: '0.95rem' }}>
                      <i className="ri-group-line me-2" style={{ color: COLORS.success }}></i>
                      Nouveaux membres récents
                    </h6>
                    <span className="badge" style={{ background: COLORS.primary, color: 'white', padding: '0.25rem 0.6rem', borderRadius: '30px', fontSize: '0.7rem' }}>
                      {data.nouveaux_recents?.length || 0} nouveaux
                    </span>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  <div className="table-responsive">
                    <Table hover className="mb-0 align-middle" style={{ fontSize: '0.85rem' }}>
                      <thead style={{ background: '#f8fafc' }}>
                        <tr>
                          <th className="px-3 py-2" style={{ color: '#475569', fontWeight: '600', fontSize: '0.7rem', textTransform: 'uppercase' }}>Matricule</th>
                          <th className="px-3 py-2" style={{ color: '#475569', fontWeight: '600', fontSize: '0.7rem', textTransform: 'uppercase' }}>Membre</th>
                          <th className="px-3 py-2" style={{ color: '#475569', fontWeight: '600', fontSize: '0.7rem', textTransform: 'uppercase' }}>Genre</th>
                          <th className="px-3 py-2" style={{ color: '#475569', fontWeight: '600', fontSize: '0.7rem', textTransform: 'uppercase' }}>Date</th>
                          <th className="px-3 py-2 text-end" style={{ color: '#475569', fontWeight: '600', fontSize: '0.7rem', textTransform: 'uppercase' }}>Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentItems.length > 0 ? (
                          currentItems.map((membre, index) => (
                            <tr key={index}>
                              <td className="px-3 py-2">
                                <span className="reference-badge">{membre.matricule || 'N/A'}</span>
                              </td>
                              <td className="px-3 py-2">
                                <div className="d-flex align-items-center gap-2">
                                  <div className="member-avatar" style={{
                                    width: '30px', height: '30px', borderRadius: '6px',
                                    background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: 'white', fontWeight: '600', fontSize: '0.7rem'
                                  }}>
                                    {membre.prenom?.charAt(0)}{membre.nom?.charAt(0)}
                                  </div>
                                  <span style={{ fontWeight: '500' }}>{membre.prenom} {membre.nom}</span>
                                </div>
                              </td>
                              <td className="px-3 py-2">
                                <span className="badge rounded-pill" style={{
                                  background: membre.gendre?.toLowerCase() === 'homme' ? COLORS.info :
                                            membre.gendre?.toLowerCase() === 'femme' ? COLORS.pink :
                                            COLORS.warning,
                                  color: 'white', padding: '0.2rem 0.6rem', fontSize: '0.7rem'
                                }}>
                                  {membre.gendre}
                                </span>
                              </td>
                              <td className="px-3 py-2">
                                <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                                  <i className="ri-calendar-line me-1"></i>
                                  {formatDate(membre.date_creation)}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-end">
                                <span className="badge rounded-pill" style={{
                                  background: `${COLORS.success}20`, color: COLORS.success,
                                  padding: '0.2rem 0.6rem', fontSize: '0.7rem'
                                }}>
                                  Actif
                                </span>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="text-center py-4 text-muted small">
                              <i className="ri-inbox-line fs-2 mb-2 d-block opacity-50"></i>
                              Aucun nouveau membre récent
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
                {totalPages > 1 && (
                  <Card.Footer style={{ background: 'rgba(255,255,255,0.5)', borderTop: '1px solid rgba(0,0,0,0.04)', padding: '0.75rem 1.25rem' }}>
                    <div className="d-flex justify-content-between align-items-center">
                      <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                        {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, data.nouveaux_recents?.length || 0)} sur {data.nouveaux_recents?.length || 0}
                      </small>
                      <Pagination className="mb-0 modern-pagination" size="sm">
                        <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                        {renderPagination()}
                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                        <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
                      </Pagination>
                    </div>
                  </Card.Footer>
                )}
              </Card>
            </Col>
          </Row>

          <Footer />
        </div>
      </div>

      {/* ===== STYLES ===== */}
      <style>{`
        .fade-in {
          animation: fadeIn 0.3s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .header-modern {
          background: white;
          padding: 1.2rem 1.8rem;
          box-shadow: 0 2px 8px rgba(0,0,0,0.04);
          border-left: 4px solid ${COLORS.secondary};
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
        }
        .header-left {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .header-icon-wrapper {
          width: 48px;
          height: 48px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.4rem;
        }
        .gradient-text {
          color: #1e293b;
          font-size: 1.3rem;
          font-weight: 700;
          margin: 0;
        }
        .badge-modern {
          background: #f1f4f9;
          color: #475569;
          padding: 0.3rem 0.8rem;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .badge-modern i {
          color: ${COLORS.secondary};
        }
        .btn-modern-outline {
          border: 1px solid #d1d5db;
          background: white;
          padding: 0.4rem 1.2rem;
          border-radius: 4px;
          font-size: 0.85rem;
          font-weight: 500;
          color: #4b5563;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .btn-modern-outline:hover {
          border-color: ${COLORS.secondary};
          color: ${COLORS.secondary};
          background: #fafafa;
        }

        .kpi-card {
          background: white;
          border-radius: 0px;
          padding: 1rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          transition: all 0.2s ease;
          height: 100%;
          border-left: 2px solid;
        }
        .kpi-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.08);
        }
        .kpi-primary { border-left-color: ${COLORS.primary}; }
        .kpi-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .kpi-primary .kpi-icon { background: rgba(41, 90, 167, 0.1); color: ${COLORS.primary}; }
        .kpi-content { flex: 1; }
        .kpi-label {
          font-size: 0.7rem;
          text-transform: uppercase;
          color: #6c757d;
          font-weight: 600;
          letter-spacing: 0.5px;
        }
        .kpi-value {
          font-size: 1.3rem;
          font-weight: 700;
          margin: 0.25rem 0;
          color: #1a3c5e;
        }
        .fs-24 { font-size: 1.5rem; }

        .stat-card-modern {
          transition: all 0.2s ease;
        }
        .stat-card-modern:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.08) !important;
        }

        .chart-card-modern {
          transition: all 0.3s ease;
        }
        .chart-card-modern:hover {
          box-shadow: 0 16px 40px rgba(0,0,0,0.08) !important;
        }

        .table-card-modern {
          transition: all 0.3s ease;
        }
        .table-card-modern:hover {
          box-shadow: 0 16px 40px rgba(0,0,0,0.08) !important;
        }

        .reference-badge {
          background: #f8f9fa;
          padding: 0.2rem 0.6rem;
          border-radius: 6px;
          font-size: 0.7rem;
          font-family: monospace;
        }

        .modern-pagination .page-link {
          border: none;
          margin: 0 2px;
          border-radius: 6px;
          color: #64748b;
          font-size: 0.75rem;
          padding: 0.3rem 0.6rem;
        }
        .modern-pagination .page-link:hover {
          background: ${COLORS.primary};
          color: white;
        }
        .modern-pagination .page-item.active .page-link {
          background: ${COLORS.primary};
          color: white;
        }

        @media (max-width: 768px) {
          .header-modern {
            flex-direction: column;
            align-items: stretch;
            gap: 0.8rem;
            padding: 1rem;
          }
          .header-left {
            flex-wrap: wrap;
          }
        }
      `}</style>
    </>
  );
}